import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { client } from '../../api/client';
import { formatDate, formatDateTime } from '../../lib/localized';

interface Lead {
  id: number;
  name: string | null;
  email: string;
  phone: string | null;
  leadType: string;
  source: string | null;
  status: string;
  assignedAdminId: number | null;
  notes: string | null;
  referralCode: string | null;
  createdAt: number;
}

const statusOptions = ['new', 'contacted', 'qualified', 'converted', 'archived'];
const typeOptions = ['experience_inquiry', 'island_owner_talk', 'inspiration_guide'];

export default function LeadsSection() {
  const { t } = useTranslation('admin');
  const [leads, setLeads] = useState<Lead[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [notesId, setNotesId] = useState<number | null>(null);
  const [notesText, setNotesText] = useState('');
  const limit = 20;

  useEffect(() => {
    fetchLeads();
  }, [statusFilter, typeFilter, page]);

  async function fetchLeads() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', String(limit));
      params.append('offset', String((page - 1) * limit));
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('lead_type', typeFilter);
      const res = await client.api.fetch(`/api/admin/leads?${params.toString()}`);
      const json = await res.json();
      setLeads(json.data || []);
      setTotal(json.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function updateStatus(id: number, status: string) {
    try {
      await client.api.fetch(`/api/admin/leads/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      fetchLeads();
    } catch (err) {
      console.error(err);
    }
  }

  async function saveNotes(id: number) {
    try {
      await client.api.fetch(`/api/admin/leads/${id}/notes`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: notesText }),
      });
      setNotesId(null);
      fetchLeads();
    } catch (err) {
      console.error(err);
    }
  }

  async function convertLead(id: number) {
    if (!confirm(t('leads.convertConfirm'))) return;
    try {
      const res = await client.api.fetch(`/api/admin/leads/${id}/convert`, { method: 'POST' });
      if (!res.ok) throw new Error('Convert failed');
      fetchLeads();
    } catch (err) {
      console.error(err);
      alert(t('leads.convertError'));
    }
  }

  const totalPages = Math.ceil(total / limit) || 1;

  const statusLabelMap: Record<string, string> = {
    new: t('leads.status.new'),
    contacted: t('leads.status.contacted'),
    qualified: t('leads.status.qualified'),
    converted: t('leads.status.converted'),
    archived: t('leads.status.archived'),
  };

  const typeLabelMap: Record<string, string> = {
    experience_inquiry: t('leads.type.experienceInquiry'),
    island_owner_talk: t('leads.type.islandOwnerTalk'),
    inspiration_guide: t('leads.type.inspirationGuide'),
  };

  const sourceLabelMap: Record<string, string> = {
    plan_page: t('leads.source.planPage'),
    invest_page: t('leads.source.investPage'),
    homepage: t('leads.source.homepage'),
    experiences_page: t('leads.source.experiencesPage'),
    properties_page: t('leads.source.propertiesPage'),
    retreats_page: t('leads.source.retreatsPage'),
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-sm p-4 flex flex-wrap items-center gap-4">
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
          <option value="">{t('leads.filters.allStatuses')}</option>
          {statusOptions.map((s) => <option key={s} value={s}>{statusLabelMap[s] || s}</option>)}
        </select>
        <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }} className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
          <option value="">{t('leads.filters.allTypes')}</option>
          {typeOptions.map((type) => <option key={type} value={type}>{typeLabelMap[type] || type}</option>)}
        </select>
        <span className="text-sm text-gray-500">{t('common.totalCount', { count: total })}</span>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">{t('leads.columns.nameEmail')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">{t('leads.columns.phone')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">{t('leads.columns.type')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">{t('leads.columns.source')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">{t('leads.columns.referralCode')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">{t('leads.columns.status')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">{t('leads.columns.notes')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">{t('leads.columns.createdAt')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-gray-500">{t('common.loading')}</td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-gray-500">{t('leads.empty')}</td></tr>
              ) : (
                leads.map((l) => (
                  <tr key={l.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-[#0d1b2a]">{l.name || '—'}</p>
                      <p className="text-xs text-gray-500">{l.email}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{l.phone || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{typeLabelMap[l.leadType] || l.leadType}</td>
                    <td className="px-4 py-3 text-gray-600">{(l.source && sourceLabelMap[l.source]) || l.source || '—'}</td>
                    <td className="px-4 py-3">
                      {l.referralCode ? (
                        <span className="font-mono text-xs text-[#0a4c6b]">{l.referralCode}</span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={l.status}
                        onChange={(e) => updateStatus(l.id, e.target.value)}
                        className="border border-gray-200 rounded-lg px-2 py-1 text-xs"
                      >
                        {statusOptions.map((s) => <option key={s} value={s}>{statusLabelMap[s] || s}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      {notesId === l.id ? (
                        <div className="flex gap-2">
                          <textarea
                            value={notesText}
                            onChange={(e) => setNotesText(e.target.value)}
                            rows={2}
                            className="border rounded-lg px-2 py-1 text-xs flex-1"
                          />
                          <div className="flex flex-col gap-1">
                            <button onClick={() => saveNotes(l.id)} className="text-xs bg-[#0a4c6b] text-white px-2 py-1 rounded">{t('common.save')}</button>
                            <button onClick={() => setNotesId(null)} className="text-xs text-gray-500">{t('common.cancel')}</button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => { setNotesId(l.id); setNotesText(l.notes || ''); }}
                          className="text-left text-xs text-gray-600 hover:text-[#0a4c6b] line-clamp-2"
                        >
                          {l.notes || <span className="text-gray-400">{t('leads.addNotes')}</span>}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{formatDate(l.createdAt)}</td>
                    <td className="px-4 py-3">
                      {l.status === 'converted' ? (
                        <span className="text-xs text-green-600 font-medium">{t('leads.converted')}</span>
                      ) : (
                        <button
                          onClick={() => convertLead(l.id)}
                          className="text-xs bg-[#0a4c6b] text-white px-2 py-1 rounded hover:bg-[#083d56] transition"
                        >
                          {t('leads.convertToCustomer')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-4 py-3 border-t flex items-center justify-between">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="text-xs bg-gray-100 px-3 py-1.5 rounded disabled:opacity-50">{t('common.previous')}</button>
            <span className="text-sm text-gray-600">{t('common.pageOf', { page, total: totalPages })}</span>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="text-xs bg-gray-100 px-3 py-1.5 rounded disabled:opacity-50">{t('common.next')}</button>
          </div>
        )}
      </div>
    </div>
  );
}
